// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { AutoSizer, Column, SortDirection } from 'react-virtualized';

import type { Dispatch, MetadataDefinition } from 'state/types';
import {
  getNewSelectionState,
  getSelectedIndices,
  reindexSelectedItems,
  type SelectionState,
  type SortDirectionType,
} from 'util/tableUtils';

import DialogPanel from 'view/App/DialogPanels/DialogPanel';
import {
  hideDialogPanel,
  showDialogPanel,
} from 'state/commands/dialogPanelCommands';
import {
  deleteMetadataDefinition as deleteMetadataDefinitionAction,
  getMetadataDefinitions as getMetadataDefinitionsAction,
} from 'state/commands/catalogCommands';
import { getActivePanelId, getMetadataDefinitions } from 'state/selectors';
import { METADATA_PANEL_ID, CREATE_METADATA_PANEL_ID } from 'util/constants';
import { metadataTypeToLocalizedString } from 'util/fieldsPageUtils';
import localizedStrings from 'util/localizedStrings';
import { bindActionCreatorsSafe } from 'util/safeDispatch';
import MultipleSelectionTable from 'view/App/MultipleSelectionTable';
import PageHeader from 'view/Pages/PageHeader';

type Props = {
  activePanelId: ?string,
  deleteMetadataDefinitionAction: typeof deleteMetadataDefinitionAction,
  getMetadataDefinitionsAction: typeof getMetadataDefinitionsAction,
  hideDialogPanel: typeof hideDialogPanel,
  intl: IntlShape,
  metadataDefinitions: MetadataDefinition[],
  showDialogPanel: typeof showDialogPanel,
};

type State = {
  selectionState: SelectionState,
  sortBy: string,
  sortDirection: SortDirectionType,
  sortedMetadataDefinitions: MetadataDefinition[],
};

class MetadataDefinitionsDialog extends Component<Props, State> {
  createMetadataDefinition: Function;
  dataTypeCellRenderer: Function;
  getSelectedMetadataDefinition: Function;
  handleScrollToChange: Function;
  handleSelectionChanged: Function;
  onRowClick: Function;
  removeMetadataDefinition: Function;
  sort: Function;

  state = {
    selectionState: getNewSelectionState(),
    sortBy: 'displayName',
    sortDirection: SortDirection.ASC,
    sortedMetadataDefinitions: [],
  };

  constructor() {
    super();
    this.createMetadataDefinition = this.createMetadataDefinition.bind(this);
    this.dataTypeCellRenderer = this.dataTypeCellRenderer.bind(this);
    this.getSelectedMetadataDefinition = this.getSelectedMetadataDefinition.bind(
      this
    );
    this.handleSelectionChanged = this.handleSelectionChanged.bind(this);
    this.removeMetadataDefinition = this.removeMetadataDefinition.bind(this);
    this.sort = this.sort.bind(this);
  }

  componentWillReceiveProps(nextProps, stateProps) {
    if (this.props.metadataDefinitions !== nextProps.metadataDefinitions) {
      const sortedMetadataDefinitions = this.sortMetadataDefinitions(
        nextProps.metadataDefinitions,
        this.state.sortBy,
        this.state.sortDirection
      );
      this.setState({
        sortedMetadataDefinitions,
        selectionState: reindexSelectedItems(
          sortedMetadataDefinitions,
          this.state.selectionState
        ),
      });
    }
  }

  componentDidMount() {
    this.props.getMetadataDefinitionsAction();
  }

  dataTypeCellRenderer({ rowData }) {
    return this.props.intl.formatMessage(
      metadataTypeToLocalizedString(rowData.dataType)
    );
  }

  sortMetadataDefinitions(metadataDefs, sortBy, sortDirection) {
    const {
      intl: { formatMessage },
    } = this.props;
    const sorted = metadataDefs.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      if (sortBy === 'dataType') {
        aVal = formatMessage(metadataTypeToLocalizedString(aVal));
        bVal = formatMessage(metadataTypeToLocalizedString(bVal));
      }
      return aVal.localeCompare(bVal);
    });

    return sortDirection === SortDirection.ASC ? sorted : sorted.reverse();
  }

  sort({ sortBy, sortDirection }) {
    const sortedMetadataDefinitions = this.sortMetadataDefinitions(
      this.props.metadataDefinitions,
      sortBy,
      sortDirection
    );
    this.setState({
      selectionState: reindexSelectedItems(
        sortedMetadataDefinitions,
        this.state.selectionState
      ),
      sortedMetadataDefinitions,
      sortBy,
      sortDirection,
    });
  }

  handleSelectionChanged(selectionState: SelectionState) {
    this.setState({ selectionState });
  }

  removeMetadataDefinition() {
    const md: MetadataDefinition = this.getSelectedMetadataDefinition();
    this.props.deleteMetadataDefinitionAction(md.id);
  }

  createMetadataDefinition() {
    this.props.showDialogPanel(CREATE_METADATA_PANEL_ID);
  }

  getSelectedMetadataDefinition(): ?MetadataDefinition {
    const { selectionState, sortedMetadataDefinitions } = this.state;
    const selectedIndices = getSelectedIndices(selectionState);
    if (selectedIndices.length === 1) {
      const selectedMetadataDef = sortedMetadataDefinitions[selectedIndices[0]];
      return selectedMetadataDef;
    }
  }

  renderPanelHeader() {
    const {
      intl: { formatMessage },
    } = this.props;
    const selectedMetadataDef: ?MetadataDefinition = this.getSelectedMetadataDefinition();
    return (
      <div data-test-name="dialog-panel-modal">
        <PageHeader
          deleteDisabled={
            !selectedMetadataDef || !selectedMetadataDef.isEditable
          }
          onClickAdd={this.createMetadataDefinition}
          onClickDelete={this.removeMetadataDefinition}
          title={formatMessage(
            localizedStrings.FIELD_METADATA_DEFINITIONS_TITLE
          )}
        />
      </div>
    );
  }

  render() {
    const {
      activePanelId,
      hideDialogPanel,
      intl: { formatMessage },
    } = this.props;

    const {
      selectionState,
      sortedMetadataDefinitions,
      sortBy,
      sortDirection,
    } = this.state;

    return (
      <DialogPanel
        activePanelId={activePanelId}
        contentStyle={{ width: '850px', height: null }}
        hideDialogPanel={hideDialogPanel}
        panelHeader={this.renderPanelHeader()}
        panelId={METADATA_PANEL_ID}
        showCancel={false}
        customButtonText={formatMessage(localizedStrings.OK)}
      >
        <div style={{ height: '500px', width: '850px' }}>
          <AutoSizer>
            {({ height, width }) => (
              <MultipleSelectionTable
                headerHeight={40}
                height={height}
                onSelectionChanged={this.handleSelectionChanged}
                rowHeight={30}
                selectionState={selectionState}
                singleSelectionOnly
                sort={this.sort}
                sortBy={sortBy}
                sortDirection={sortDirection}
                sortedItems={sortedMetadataDefinitions}
                width={width}
              >
                <Column
                  className="list-item"
                  label={formatMessage(
                    localizedStrings.FIELD_METADATA_NAME_LABEL
                  )}
                  dataKey="displayName"
                  width={300}
                />
                <Column
                  className="list-item"
                  dataKey="tag"
                  label={formatMessage(
                    localizedStrings.FIELD_METADATA_TAG_LABEL
                  )}
                  width={300}
                />
                <Column
                  className="list-item"
                  dataKey="type"
                  label={formatMessage(
                    localizedStrings.FIELD_METADATA_TYPE_LABEL
                  )}
                  width={100}
                />
                <Column
                  className="list-item"
                  dataKey="dataType"
                  label={formatMessage(
                    localizedStrings.FIELD_METADATA_DATA_TYPE_LABEL
                  )}
                  cellRenderer={this.dataTypeCellRenderer}
                  width={175}
                />
              </MultipleSelectionTable>
            )}
          </AutoSizer>
        </div>
      </DialogPanel>
    );
  }
}

export default connect(
  state => ({
    activePanelId: getActivePanelId(state),
    metadataDefinitions: getMetadataDefinitions(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsSafe({
      deleteMetadataDefinitionAction,
      getMetadataDefinitionsAction,
      hideDialogPanel,
      showDialogPanel,
    })
)(injectIntl(MetadataDefinitionsDialog));



// WEBPACK FOOTER //
// ./src/view/Pages/CatalogsPage/MetadataDefinitionsDialog.js